import { useState, useEffect } from "react";
import { ArrowLeft, Mail, Phone, Building, User, MessageSquare, Calendar, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] font-body" data-testid="admin-leads-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-gray-600 hover:text-[#D00000] transition-colors" data-testid="back-link">
                <ArrowLeft size={20} />
                <span className="text-sm font-medium">Back to Site</span>
              </a>
              <span className="text-gray-300">|</span>
              <h1 className="font-heading text-xl uppercase tracking-tight text-[#0A0A0A]">Lead Dashboard</h1>
            </div>
            <Button 
              onClick={fetchLeads}
              variant="outline"
              className="border-2 border-gray-200 hover:border-[#D00000] rounded-none px-4 py-2 text-sm font-medium flex items-center gap-2"
              data-testid="refresh-button"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border border-gray-200" data-testid="total-leads-stat">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Total Leads</p>
            <p className="font-heading text-4xl text-[#0A0A0A]">{leads.length}</p>
          </div>
          <div className="bg-white p-6 border border-gray-200" data-testid="today-leads-stat">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Today</p>
            <p className="font-heading text-4xl text-[#D00000]">
              {leads.filter(l => {
                const leadDate = new Date(l.created_at);
                const today = new Date();
                return leadDate.toDateString() === today.toDateString();
              }).length}
            </p>
          </div>
          <div className="bg-white p-6 border border-gray-200" data-testid="this-week-stat">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">This Week</p>
            <p className="font-heading text-4xl text-[#0A0A0A]">
              {leads.filter(l => {
                const leadDate = new Date(l.created_at);
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return leadDate >= weekAgo;
              }).length}
            </p>
          </div>
        </div>

        {/* Leads List */}
        <div className="bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="font-heading text-lg uppercase tracking-tight text-[#0A0A0A]">All Leads</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center" data-testid="loading-state">
              <RefreshCw size={32} className="animate-spin text-[#D00000] mx-auto mb-4" />
              <p className="text-gray-500">Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <p className="text-gray-500">No leads yet. They'll appear here when someone submits the contact form.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200" data-testid="leads-list">
              {leads.map((lead) => (
                <div 
                  key={lead.id}
                  className={`p-6 hover:bg-[#F4F4F5] cursor-pointer transition-colors ${selectedLead?.id === lead.id ? 'bg-[#F4F4F5]' : ''}`}
                  onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                  data-testid={`lead-item-${lead.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Building size={16} className="text-[#D00000]" />
                        <h3 className="font-bold text-[#0A0A0A]">{lead.business_name}</h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {lead.contact_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {lead.email}
                        </span>
                        {lead.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {lead.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Calendar size={14} />
                      {formatDate(lead.created_at)}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedLead?.id === lead.id && lead.message && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-start gap-2">
                        <MessageSquare size={16} className="text-[#D00000] mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Message</p>
                          <p className="text-sm text-gray-600">{lead.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
